import {DateUtils, Patient} from "@allaihealth/util";
import {IsActiveMatchOptions, Router, UrlTree} from "@angular/router";
import {NgZone} from "@angular/core";

/** Various utility functions for the application */
export abstract class Utils {
	/** Attempt to get the duration of an audio blob. This won't always work due to the way web audio works */
	static getAudioBlobDuration(blob:Blob) {
		return new Promise<number>((resolve, reject) => {
			const audio = new Audio(URL.createObjectURL(blob));

			audio.addEventListener("loadedmetadata", () => {
				if(isFinite(audio.duration) && audio.duration > 0) {
					resolve(audio.duration);
				}

				reject("Failed to get duration");
			}, {once: true});

			audio.addEventListener("error", event => {
				reject(event);
			});
		})
	}

	/** Get whether this patient should be prefixed by "mr" or "ms", or "" if sex can't be determined */
	static getMrMs(patient:Patient):string {
		switch(patient.sex?.toLowerCase()) {
			case "male":
				return "Mr.";
			case "female":
				return "Ms.";
			default:
				return "";
		}
	}

	/** Get the age and sex of the patient for the header */
	static getAgeAndSex(patient:Patient) {
		let sex:string;
		switch(patient.sex?.toLowerCase()) {
			case "male":
				sex = "M";
				break;
			case "female":
				sex = "F";
				break;
			default:
				sex = " yo";
				break;
		}

		return `${DateUtils.getAge(patient)}${sex}`
	}

	/**
	 * Test a parent route to see if a child is active. For example if the passed in route is "/a/b" this will return
	 * true if the current route is "/a/b/c" or "/a/b/d", but not if the route is "/a/b".
	 * @param router angular router to use for testing
	 * @param route the route to check and see if a child is active
	 */
	static testRouteChildActive(router:Router, route:string | string[]) {
		let url:string | UrlTree = Array.isArray(route) ? router.createUrlTree(route) : route;

		const parent:IsActiveMatchOptions = {
			paths: 'exact',
			queryParams: 'ignored',
			fragment: 'ignored',
			matrixParams: 'ignored'
		};

		const child:IsActiveMatchOptions = {
			paths: 'subset',
			queryParams: 'ignored',
			fragment: 'ignored',
			matrixParams: 'ignored'
		};

		return !router.isActive(url, parent) && router.isActive(url, child);
	}

	/** Wait for an Angular zone and then continue executing */
	static waitForAngularZone(ngZone:NgZone) {
		return new Promise<void>(resolve => {
			ngZone.run(() => {
				resolve();
			});
		});
	}

	/** Get the name of a file (with extension) from a URI */
	static getFilenameFromUri(uri:string) {
		const path = new URL(uri);
		const parts = path.pathname.split('/');
		return parts[parts.length - 1];
	}
}
